import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { createClient } from '@supabase/supabase-js';
import { supabase, supabaseUrl, supabaseAnonKey } from '../../lib/supabaseClient';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select'; 
import { AlertCircle, UserPlus, Edit, Trash2 } from 'lucide-react'; // Added Edit and Trash2 icons
import EditUserModal from './components/EditUserModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { useSelector } from 'react-redux';

const UserRegistration = () => {
  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors, isSubmitting } } = useForm();
  const selectedRole = watch('role');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]); // State for eligible employees
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [users, setUsers] = useState([]); // New state for storing fetched users
  const [loadingUsers, setLoadingUsers] = useState(true); // New state for user loading status
  const [showEditUserModal, setShowEditUserModal] = useState(false); // State to control edit modal visibility
  const [editingUser, setEditingUser] = useState(null); // State to store the user being edited
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false); // State to control delete confirmation modal visibility
  const [userToDelete, setUserToDelete] = useState(null); // State to store the user to be deleted

  // Get actual user from Redux store
  const { user } = useSelector((state) => state.auth);
  const canManageUsers = user?.role === 'admin' || user?.role === 'system_admin';

  const filteredEmployees = employees.filter(emp => {
    const matchesDept = departmentFilter === 'All' || (emp.department_id != null && String(emp.department_id) === String(departmentFilter));
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      emp.full_name?.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower) ||
      (emp.employee_number && emp.employee_number.toString().toLowerCase().includes(searchLower));

    return matchesDept && matchesSearch;
  });

  // Fetch eligible employees (those who don't have a system profile yet)
  const fetchEligibleEmployees = async () => {
    try {
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('id, full_name, email, department_id, employee_number')
        .order('full_name');

      if (employeesError) throw employeesError;

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('email');

      if (profilesError) throw profilesError;

      const existingEmails = new Set(profilesData.map(p => p.email?.toLowerCase()));
      const eligible = employeesData.filter(emp => 
        emp.email && !existingEmails.has(emp.email.toLowerCase())
      );

      setEmployees(eligible);
    } catch (err) {
      console.error('Error fetching eligible employees:', err);
    }
  };

  // Fetch departments for the select dropdown
  useEffect(() => {
    const fetchDepartments = async () => {
      const { data, error } = await supabase.from('departments').select('id, name');
      if (error) {
        console.error('Error fetching departments:', error);
      } else {
        setDepartments(data.map(dept => ({ value: dept.id, label: dept.name })));
      }
    };
    fetchDepartments();
    fetchEligibleEmployees();
  }, []);

  // Fetch users from the profiles table
  const fetchUsers = async () => {
    setLoadingUsers(true);
    fetchEligibleEmployees(); // Also refresh eligible employees
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        full_name,
        email,
        role,
        department_id,
        departments (name)
      `)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users.');
    } else {
      setUsers(data);
    }
    setLoadingUsers(false);
  };

  useEffect(() => {
    fetchUsers(); // Initial fetch of users
  }, []);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.employee-combobox')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Define allowed roles for registration (MUST match database check constraints)
  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'it_staff', label: 'IT Staff' },
    { value: 'department_pic', label: 'Department PIC' },
  ];

  const onSubmit = async (data) => {
    setSuccess('');
    setError('');

    // Validate selected employee
    if (!selectedEmployee) {
      setError("Please select an employee from the list.");
      return;
    }

    try {
      //Create a temporary Supabase client to prevent session hijacking
      const tempAuthClient = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      });

      // Create user in Supabase auth.users using the temp client
      const { data: authData, error: authError } = await tempAuthClient.auth.signUp({
        email: selectedEmployee.email,
        password: data.password,
        options: {
          data: {
            full_name: selectedEmployee.full_name,
            role: data.role
          }
        }
      });
      
      if (authError) throw authError;

      //UPSERT corresponding profile entry into 'profiles' table using the MAIN client
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authData.user.id,
          full_name: selectedEmployee.full_name,
          role: data.role,
          email: selectedEmployee.email,
          department_id: data.role === 'department_pic' ? parseInt(data.department_id) : selectedEmployee.department_id,
        }, { onConflict: 'id' });

      if (profileError) {
        throw profileError;
      }

      // Trigger success notification and reset form
      setSuccess(`User ${selectedEmployee.email} registered successfully.`);
      reset();
      setSearchTerm('');
      setSelectedEmployee(null);
      fetchUsers(); // Refresh the list

    } catch (err) {
      setError(err.message);
      console.error("Registration error:", err);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-3 text-foreground">
        <UserPlus size={28} /> User Registration
      </h1>
      <p className="text-muted-foreground mb-8">
        Use this form to provision new user accounts and assign their access roles (Admin, IT Staff, or Department PIC) within the system.
      </p>

      {success && (
        <div className="bg-green-100 text-green-700 p-3 rounded-lg mb-4">{success}</div>
      )}
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {canManageUsers ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-card p-8 rounded-xl shadow-lg border border-border">
            <div className="relative employee-combobox z-20">
              <div className="flex flex-col sm:flex-row gap-4 mb-4 items-start sm:items-start">
                <div className="w-full sm:w-1/3">
                  <Select
                    label="Filter by Dept"
                    value={departmentFilter}
                    onChange={(val) => {
                      setDepartmentFilter(val);
                      setIsDropdownOpen(true);
                    }}
                    options={[{ value: 'All', label: 'All Departments' }, ...departments]}
                    placeholder="All Departments"
                  />
                </div>
                <div className="w-full sm:w-2/3 relative">
                  <Input 
                    label="Search Employee"
                    type="text" 
                    placeholder="Name, email, or ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setIsDropdownOpen(true);
                      if (selectedEmployee) setSelectedEmployee(null); // Reset selection if typing
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    autoComplete="off"
                    className="bg-card"
                  />
                  <div className="absolute bottom-2.5 right-3 pointer-events-none text-muted-foreground hidden sm:block">
                    <UserPlus size={18} />
                  </div>
                </div>
              </div>

              {/* The Dropdown List */}
              {isDropdownOpen && (searchTerm.length > 0 || departmentFilter !== 'All') && (
                <ul className="absolute z-50 w-full bg-card border border-border mt-1 max-h-60 overflow-y-auto shadow-xl rounded-lg divide-y divide-gray-50">
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map(emp => (
                      <li 
                        key={emp.id}
                        className="p-3 hover:bg-blue-50 cursor-pointer transition-colors flex flex-col"
                        onClick={() => {
                          setSelectedEmployee(emp);
                          setSearchTerm(emp.full_name); // Fill input with selected name
                          setIsDropdownOpen(false);     // Close dropdown
                        }}
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-bold text-foreground text-sm">{emp.full_name}</span>
                          <span className="text-[10px] font-mono bg-muted/50 px-1.5 py-0.5 rounded text-muted-foreground">
                            ID: {emp.employee_number || 'N/A'}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">{emp.email}</div>
                      </li>
                    ))
                  ) : (
                    <li className="p-4 text-center text-sm text-muted-foreground italic">
                      No matching employees found.
                    </li>
                  )}
                </ul>
              )}
              {selectedEmployee && (
                <div className="mt-2 text-[11px] font-bold text-primary flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
                  Selected: {selectedEmployee.full_name} ({selectedEmployee.email})
                </div>
              )}
            </div>
            <Input
              label="Temporary Password"
              type="password"
              placeholder="Min 6 characters"
              {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })}
              error={errors.password?.message}
              className="bg-card"
            />
            <Controller
              name="role"
              control={control}
              rules={{ required: 'Role assignment is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Assign Role"
                  options={roles}
                  placeholder="Select a role..."
                  error={errors.role?.message}
                />
              )}
            />
            {selectedRole === 'department_pic' && (
              <Controller
                name="department_id"
                control={control}
                rules={{ required: selectedRole === 'department_pic' ? 'Department is required for PIC' : false }}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Assign Department"
                    options={departments}
                    placeholder="Select a department..."
                    error={errors.department_id?.message}
                  />
                )}
              />
            )}
            <Button 
              type="submit" 
              loading={isSubmitting} 
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Registering User...' : 'Register User'}
            </Button>
          </form>
        ) : (
          <div className="bg-muted/30 p-8 rounded-xl border border-dashed border-border flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <AlertCircle size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Registration Restricted</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              Only system administrators can provision new user accounts. Your current role permits view-only access to the user list.
            </p>
          </div>
        )}

        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-5 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground">Registered Users</h2>
          </div>

          {loadingUsers ? (
            <div className="text-center py-10 text-muted-foreground/70">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground/70">No registered users found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
                <thead className="bg-muted">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Full Name</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Role</th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">Department</th>
                    {canManageUsers && <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>}
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-muted/80 transition-colors group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-foreground">{u.full_name || 'Unnamed User'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {u.email || 'No email provided'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-primary/10 text-primary border border-primary/20 uppercase tracking-wider">
                          {u.role === 'system_admin' ? 'System Admin' : u.role === 'department_pic' ? 'Department PIC' : u.role || 'User'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {u.departments?.name || '-'}
                      </td>
                      {canManageUsers && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-primary hover:bg-blue-50"
                              onClick={() => {
                                setEditingUser(u);
                                setShowEditUserModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => {
                                setUserToDelete(u);
                                setShowDeleteConfirmModal(true);
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showEditUserModal && editingUser && (
        <EditUserModal
          user={editingUser}
          departments={departments}
          onClose={() => {
            setShowEditUserModal(false);
            setEditingUser(null);
          }}
          onUserUpdated={() => {
            fetchUsers(); // Refresh the list of users
            setSuccess('User updated successfully!');
          }}
        />
      )}

      {showDeleteConfirmModal && userToDelete && (
        <DeleteConfirmationModal
          user={userToDelete}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setUserToDelete(null);
          }}
          onConfirmDelete={() => {
            fetchUsers(); // Refresh the list of users
            setSuccess('User deleted successfully!');
          }}
        />
      )}
    </div>
  );
};


export default UserRegistration;
